import { LocalStorageKeys } from '@constants/enums';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import authManager from '@efex-services/auth/authManager';
import cognitoService from '@efex-services/auth/cognitoService';
import { isLegacyAuth } from '@efex-services/auth/authUtils';
import { client } from '@efex-services/config';
import {
  legacyTokenRefreshed,
  cognitoTokenRefreshed,
} from '@/layout/login/auth';

interface UseTokenRefreshReturn {
  refreshToken: () => Promise<boolean>;
}

/**
 * Hook to handle token refresh for both Legacy and Cognito auth
 * @returns Object with refresh function
 */
export const useTokenRefresh = (): UseTokenRefreshReturn => {
  const dispatch = useDispatch();

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await authManager.refreshToken();

      if (isLegacyAuth()) {
        if (!response.token) {
          return false;
        }

        localStorage.setItem(LocalStorageKeys.TOKEN, response.token);
        client.defaults.headers.common = {
          Authorization: `Bearer ${response.token}`,
        };

        dispatch(legacyTokenRefreshed({ token: response.token }));
        return true;
      }

      if (!response.idToken) {
        return false;
      }

      const isHandled = cognitoService.handleTokenRefresh(response.idToken);

      if (isHandled) {
        client.defaults.headers.common = {
          Authorization: `Bearer ${response.idToken}`,
        };

        dispatch(
          cognitoTokenRefreshed({
            idToken: response.idToken,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresIn: response.expiresIn,
            tokenType: response.tokenType,
          })
        );
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }, [dispatch]);

  return {
    refreshToken,
  };
};

export default useTokenRefresh;
